import React from 'react';
import { connect, ResolveThunks } from 'react-redux';
import { Box, Button, Divider, FormControl, InputLabel, MenuItem, Paper, Select, Typography } from '@mui/material';

import { enqueueError } from '@paragon/notification-tools';

import { deleteView, overwriteView, removeDefaultView, saveView, setDefaultView } from '../../../actions/viewActions';
import { PropertyKey } from '../../../propertyMetadata';
import { getActiveViewOrViewId, getDefaultViewOrViewIdForUser } from '../../../selectors/getActiveView';
import { getSortedViews } from '../../../selectors/getSortedViews';
import { RootState } from '../../../store';
import { JobFilter, noFilterView, setActiveView } from '../../../store/viewsSlice';
import ColumnsCheckboxes from './ColumnsCheckboxes';
import JobFilterComponent, { DraftJobFilter } from './JobFilter';

function getNonDraftView(draftView: DraftView) {
  return {
    ...draftView,
    jobFilters: draftView.jobFilters.filter((filter) => filter.propertyKey != null) as readonly JobFilter[],
  };
}

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ResolveThunks<typeof actionCreators>;

export type Props = StateProps & DispatchProps;

interface DraftView {
  readonly jobFilters: readonly DraftJobFilter[];
  readonly visibleColumns: readonly PropertyKey[];
}

interface State {
  selectedViewOrViewId: number | DraftView;
}

class UnconnectedViews extends React.Component<Props, State> {
  state: State = {
    selectedViewOrViewId: this.props.activeViewOrViewId,
  };

  getSelectedView(state: State) {
    if (typeof state.selectedViewOrViewId !== 'number') {
      return state.selectedViewOrViewId;
    }

    const savedView = this.props.savedViews.find((view) => view.id === state.selectedViewOrViewId);
    if (!savedView) console.error('Could not find saved view.');
    return savedView ?? null;
  }

  getSelectedNonDraftView() {
    const selectedView = this.getSelectedView(this.state);
    if (!selectedView) return null;

    return {
      ...selectedView,
      jobFilters: selectedView.jobFilters.filter((filter) => filter.propertyKey != null) as readonly JobFilter[],
    };
  }

  getSelectedNonDraftViewOrViewId() {
    if (typeof this.state.selectedViewOrViewId === 'number') {
      return this.state.selectedViewOrViewId;
    }

    return getNonDraftView(this.state.selectedViewOrViewId);
  }

  isDefaultView() {
    if (typeof this.state.selectedViewOrViewId !== 'number') return false;

    const selectedView = this.props.savedViews.find((view) => view.id === this.state.selectedViewOrViewId);
    if (!selectedView) console.error('Could not find saved view.');
    return selectedView?.isDefault ?? false;
  }

  changeView(savedViewId: number) {
    this.setState({ selectedViewOrViewId: savedViewId });
  }

  setVisibleColumns = (visibleColumns: readonly PropertyKey[]) => {
    this.setState((state) => {
      const selectedView = this.getSelectedView(state);
      return selectedView ? { selectedViewOrViewId: { ...selectedView, visibleColumns } } : null;
    });
  };

  saveView = async () => {
    const selectedView = this.getSelectedNonDraftView();
    if (!selectedView) return;

    const getName = (): string | null => {
      const name = prompt('Please enter a name for the new filter.');
      const existingView = this.props.savedViews.find((view) => view.name === name);
      if (name == null) return null;
      if (name === '') {
        this.props.enqueueError('Unable to save filter. Name was left empty.');
        return null;
      }
      if (!existingView || confirm(`Filter '${name}' already exists. Would you like to overwrite it?`)) {
        return name;
      }
      return getName();
    };
    const name = getName();
    if (name == null) return;

    const existingView = this.props.savedViews.find((view) => view.name === name);
    if (!existingView) {
      await this.props.saveView(selectedView, name);
    } else {
      await this.props.overwriteView(selectedView, name);
    }
  };

  applyView = () => {
    this.props.setActiveView(this.getSelectedNonDraftViewOrViewId());
  };

  createNewView = () => {
    this.setState({ selectedViewOrViewId: noFilterView });
    this.props.setActiveView(noFilterView);
  };

  removeDefaultView = async () => {
    await this.props.removeDefaultView();
  };

  setSelectedFilterAsDefault = async () => {
    if (typeof this.state.selectedViewOrViewId !== 'number') {
      console.error('Expected saved view.');
      return;
    }
    await this.props.setDefaultView(this.state.selectedViewOrViewId);
  };

  deleteView = () => {
    if (typeof this.state.selectedViewOrViewId !== 'number') {
      console.error('Saved view not selected');
      return;
    }

    const selectedViewId = this.state.selectedViewOrViewId;
    const selectedView = this.props.savedViews.find((view) => view.id === selectedViewId);
    if (!selectedView) {
      console.error('Could not find saved view.');
      return;
    }
    const response = confirm(`Are you sure you want to delete filter '${selectedView.name}'?`);
    if (response) {
      this.props.deleteView(selectedView.id);
      this.setState({ selectedViewOrViewId: getDefaultViewOrViewIdForUser(this.props.savedViews) });
    }
  };

  updateJobFilter = (oldJobFilter: DraftJobFilter, updatedJobFilter: DraftJobFilter) => {
    this.setState((state) => {
      const selectedView = this.getSelectedView(state);
      if (!selectedView) return null;
      const updatedJobFilters =
        oldJobFilter.propertyKey == null
          ? selectedView.jobFilters.concat(updatedJobFilter)
          : selectedView.jobFilters.map((jobFilter) => (jobFilter === oldJobFilter ? updatedJobFilter : jobFilter));
      return { selectedViewOrViewId: { ...selectedView, jobFilters: updatedJobFilters } };
    });
  };

  removeJobFilter = (jobFilterToRemove: DraftJobFilter) => {
    this.setState((state) => {
      const selectedView = this.getSelectedView(state);
      if (!selectedView) return null;
      const updatedJobFilters = selectedView.jobFilters.filter((jobFilter) => jobFilter !== jobFilterToRemove);
      return { selectedViewOrViewId: { ...selectedView, jobFilters: updatedJobFilters } };
    });
  };

  render() {
    const orderedViews = [
      ...this.props.savedViews.filter((view) => view.isDefault),
      ...this.props.savedViews.filter((view) => !view.isDefault),
    ];

    const savedViewsOptions = orderedViews.map((view) => (
      <MenuItem key={view.id} value={view.id}>
        {view.name}
      </MenuItem>
    ));

    const jobFilters = (this.getSelectedView(this.state)?.jobFilters ?? []).concat({
      propertyKey: null,
      operator: '=',
      value: '',
    });

    const jobFilterComponents = jobFilters.map((jobFilter, index) => (
      <JobFilterComponent
        key={`job-filter-${index}`}
        jobFilter={jobFilter}
        onChange={(updatedJobFilter) => this.updateJobFilter(jobFilter, updatedJobFilter)}
        onRemove={() => {
          this.removeJobFilter(jobFilter);
        }}
      />
    ));

    const viewEmpty =
      this.getSelectedNonDraftView()?.jobFilters.length === 0 &&
      this.getSelectedNonDraftView()?.visibleColumns.length === noFilterView.visibleColumns.length;

    const noSavedViewSelected = typeof this.state.selectedViewOrViewId !== 'number';

    const isDefaultView = this.isDefaultView();

    return (
      <div>
        <Paper
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            margin: 'auto',
            width: '95%',
            justifyContent: 'center',
            bgcolor: 'backContrast.main',
          }}
        >
          <FormControl variant="outlined" sx={{ m: 1, width: 200 }}>
            <InputLabel htmlFor="selectFilter">Select View</InputLabel>
            <Select<number>
              value={typeof this.state.selectedViewOrViewId === 'number' ? this.state.selectedViewOrViewId : ''}
              // MUI types are lying here, the value is a string
              onChange={(event) => this.changeView(parseInt(event.target.value as unknown as string, 10))}
              label="Select View"
              inputProps={{
                name: 'selectView',
                id: 'selectFilter',
              }}
            >
              {savedViewsOptions}
            </Select>
          </FormControl>
          <Button variant="outlined" onClick={this.applyView} sx={{ m: 1, width: 200 }} disabled={viewEmpty}>
            Apply View
          </Button>
          <Button
            variant="outlined"
            onClick={this.createNewView}
            sx={{ m: 1, width: 200 }}
            disabled={noSavedViewSelected && viewEmpty}
          >
            Create New View
          </Button>
          <Button
            variant="outlined"
            onClick={isDefaultView ? this.removeDefaultView : this.setSelectedFilterAsDefault}
            sx={{ m: 1, width: 200 }}
            disabled={noSavedViewSelected}
          >
            {isDefaultView ? 'Clear Default View' : 'Set View as Default'}
          </Button>
          <Button variant="outlined" onClick={this.deleteView} sx={{ m: 1, width: 200 }} disabled={noSavedViewSelected}>
            Delete View
          </Button>
          <Button variant="outlined" onClick={this.saveView} sx={{ m: 1, width: 200 }} disabled={viewEmpty}>
            Save View As
          </Button>
        </Paper>
        <Box sx={{ m: 'auto', mt: 2, width: '95%' }}>
          <ColumnsCheckboxes
            selectedColumnKeys={this.getSelectedView(this.state)?.visibleColumns ?? []}
            onChange={this.setVisibleColumns}
          />
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', m: 2 }}>
          <Box sx={{ width: '70%' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 4 }}>
              <Typography>Column Filters</Typography>
              <Divider sx={{ width: '75%', color: '#333' }} />
            </Box>
            {jobFilterComponents}
          </Box>
        </Box>
      </div>
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  activeViewOrViewId: getActiveViewOrViewId(state),
  savedViews: getSortedViews(state),
});

const actionCreators = {
  deleteView,
  enqueueError,
  overwriteView,
  removeDefaultView,
  saveView,
  setActiveView,
  setDefaultView,
};

export default connect(mapStateToProps, actionCreators)(UnconnectedViews);
